import * as React from "react";

import { cn } from "@/lib/utils";

export interface InputProps extends Omit<React.ComponentProps<"input">, 'size'> {
  /**
   * Size variant of the input
   * @default "md"
   */
  variant?: "xs" | "sm" | "md" | "lg";
  
  /**
   * Shape variant of the input
   * @default "default"
   */
  shape?: "default" | "square" | "round" | "pill";
  
  /**
   * Width variant - if not specified, input sizes naturally based on size attribute
   */
  width?: "full" | "auto";
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, variant = "md", shape = "default", width, ...props }, ref) => {
    
    // Size variants
    const sizeClasses = {
      xs: "h-7 px-2 py-1 text-xs",
      sm: "h-8 px-2.5 py-1.5 text-sm",
      md: "h-10 px-3 py-2 text-base md:text-sm",
      lg: "h-12 px-4 py-3 text-lg"
    };
    
    // Shape variants
    const shapeClasses = {
      default: "rounded",
      square: "rounded-none",
      round: "rounded-sm",
      pill: "rounded-full"
    };
    
    // Width variants (only applied when explicitly set)
    const widthClasses = width ? {
      full: "w-full",
      auto: "w-auto"
    }[width] : undefined;
    
    return (
      <input
        type={type}
        dir="auto"
        className={cn(
          // Base styles
          "flex border border-input bg-transparent ring-offset-background",
          "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground",
          "placeholder:text-muted-foreground",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "transition-colors",
          // Variant styles
          sizeClasses[variant],
          shapeClasses[shape],
          widthClasses,
          // Custom className override
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
Input.displayName = "Input";

export { Input };